import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';
import { resolveImage } from '../lib/api';

// Optimise external Unsplash URLs by appending width/quality params if not already set.
const optimizeImageUrl = (url, width = 1920, quality = 75) => {
  if (!url) return '';
  if (url.includes('images.unsplash.com') && !/[?&]w=/.test(url)) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}w=${width}&q=${quality}&auto=format&fit=crop`;
  }
  if (url.includes('images.pexels.com') && !/[?&]w=/.test(url)) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}auto=compress&cs=tinysrgb&w=${width}`;
  }
  return url;
};

const HeroSlider = () => {
  const { slides, settings } = useSiteData();
  const sorted = (slides || []).slice().sort((a, b) => a.order - b.order);
  const [idx, setIdx] = useState(0);
  const [loadedIds, setLoadedIds] = useState(() => new Set());
  const total = sorted.length;
  const preloadedRef = useRef(new Set());

  const next = useCallback(() => setIdx((i) => (total ? (i + 1) % total : 0)), [total]);
  const prev = () => setIdx((i) => (total ? (i - 1 + total) % total : 0));

  // Auto-advance only if more than one slide and current is loaded.
  useEffect(() => {
    if (total < 2) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, total]);

  useEffect(() => {
    if (idx >= total && total > 0) setIdx(0);
  }, [idx, total]);

  // Preload the FIRST slide image with high priority and the NEXT one with low priority,
  // so the user sees content instantly and transitions are smooth.
  useEffect(() => {
    if (sorted.length === 0) return;
    const head = document.head;
    const links = [];

    const ensurePreload = (url, priority) => {
      const finalUrl = optimizeImageUrl(resolveImage(url));
      if (!finalUrl || preloadedRef.current.has(finalUrl)) return;
      preloadedRef.current.add(finalUrl);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = finalUrl;
      if (priority) link.fetchPriority = priority;
      head.appendChild(link);
      links.push(link);
    };

    ensurePreload(sorted[0].image_url, 'high');
    if (sorted[1]) ensurePreload(sorted[1].image_url, 'low');

    return () => {
      links.forEach((l) => {
        try { head.removeChild(l); } catch { /* already removed */ }
      });
    };
  }, [sorted]);

  // When current index advances, prefetch the NEXT image so the next swap is instant.
  useEffect(() => {
    const next = sorted[(idx + 1) % Math.max(total, 1)];
    if (!next) return;
    const url = optimizeImageUrl(resolveImage(next.image_url));
    if (!url || preloadedRef.current.has(url)) return;
    preloadedRef.current.add(url);
    const img = new Image();
    img.src = url;
  }, [idx, sorted, total]);

  const onLoad = useCallback((id) => {
    setLoadedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  if (total === 0) {
    return (
      <section className="flex h-[420px] w-full items-center justify-center bg-[#0B2545] text-white">
        <div className="text-center">
          <h1 className="font-serif text-3xl font-bold">South East State of Somalia</h1>
          <p className="mt-2 text-white/70">No active slides. Add some from the admin portal.</p>
        </div>
      </section>
    );
  }

  const cur = sorted[Math.min(idx, total - 1)];
  const hb = settings?.hero_buttons || {};
  const curLoaded = loadedIds.has(cur.id);

  return (
    <section className="relative h-[640px] w-full overflow-hidden bg-[#0B2545]">
      {/* Soft loading skeleton (always behind images so the section is never blank) */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-500 ${
          curLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          background:
            'linear-gradient(135deg, #0B2545 0%, #11305c 50%, #0B2545 100%)'
        }}
      />

      {sorted.map((s, i) => {
        const isCurrent = i === idx;
        const isLoaded = loadedIds.has(s.id);
        const finalUrl = optimizeImageUrl(resolveImage(s.image_url));
        // Only render <img> for current and adjacent slides to save bandwidth.
        const adjacent =
          i === idx ||
          i === (idx + 1) % total ||
          i === (idx - 1 + total) % total;
        if (!adjacent) return null;
        return (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              isCurrent && isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={finalUrl}
              alt={s.title}
              className="h-full w-full object-cover"
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'auto'}
              decoding="async"
              onLoad={() => onLoad(s.id)}
              onError={() => onLoad(s.id)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545]/85 via-[#0B2545]/55 to-[#0B2545]/40" />
          </div>
        );
      })}

      {/* Content — always visible (so the title is readable even while image loads) */}
      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-end px-6 pb-24">
        <div className="max-w-2xl">
          <div className="mb-5 h-[3px] w-16 bg-[#C9A86A]" />
          <h1 className="font-serif text-4xl font-bold leading-tight text-white md:text-5xl">
            {cur.title}
          </h1>
          {cur.subtitle && (
            <p className="mt-4 max-w-xl text-[15px] text-white/85 md:text-base">{cur.subtitle}</p>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {hb.primary_label && (
              <Link
                to={hb.primary_link || '/'}
                className="group inline-flex items-center gap-2 rounded-md bg-[#C9A86A] px-5 py-3 text-[14px] font-medium text-white shadow-md transition hover:bg-[#b8975c]"
              >
                {hb.primary_label}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            {hb.secondary_label && (
              <Link
                to={hb.secondary_link || '/news'}
                className="inline-flex items-center gap-2 rounded-md border border-white/40 bg-white/10 px-5 py-3 text-[14px] font-medium text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                {hb.secondary_label}
              </Link>
            )}
            {hb.tertiary_label && (
              <Link
                to={hb.tertiary_link || '/investment/opportunities'}
                className="inline-flex items-center gap-2 rounded-md border border-white/40 bg-white/10 px-5 py-3 text-[14px] font-medium text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                {hb.tertiary_label}
              </Link>
            )}
          </div>
        </div>
      </div>

      {total > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous"
            className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-[#0B2545]/40 text-white backdrop-blur-md transition hover:bg-[#0B2545]/70"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            aria-label="Next"
            className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-[#0B2545]/40 text-white backdrop-blur-md transition hover:bg-[#0B2545]/70"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
            {sorted.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === idx ? 'w-8 bg-[#C9A86A]' : 'w-2 bg-white/60'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroSlider;
